// Deterministic natural-language parser for the chat assistant. Given a
// user message and the customer's commodities/locations/counterparties,
// it produces a ChatQueryFilters object plus the descriptor that should
// handle it. No LLM is involved - we lean on the same fuzzy matcher the
// rest of ChatAssistant.tsx already uses for upload mapping.
//
// Time-dimension rules (driven by user direction):
//   - "trade date", "traded on", "trades from..." -> tradeDate filter,
//     useTradeDate = true. Bare years are still expanded into the
//     fiscal-year range so e.g. "trades in 2026" works.
//   - everything else -> budget-month range derived from
//     customerSettings.fiscalMonthOffset via ChatFiscalYear.

import dayjs from 'dayjs';
import type {
    CommodityModel,
    CounterpartyModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    LocationModel,
    PortfolioModel,
    PositionType,
} from '../../../../Generated/Commodity-Management-Api';
import {
    expandFiscalYearToBudgetMonthRange,
    parseAllFiscalYearsFromMessage,
    type FiscalYearRange,
} from './ChatFiscalYear';
import {
    CHAT_QUERY_REGISTRY,
    pickQueryDescriptor,
    type ChatQueryDescriptor,
    type ChatQueryFilters,
} from './ChatQueryRegistry';

export interface ParsedChatQuery {
    descriptor: ChatQueryDescriptor;
    filters: ChatQueryFilters;
    // Names the parser tried to resolve to a customer dimension but
    // couldn't match. The orchestrator surfaces these so the user knows
    // why a filter wasn't applied.
    unresolvedNames: string[];
    // Echo of which fiscal-year ranges were used so the chat can show
    // the user exactly which months it queried.
    fiscalYearRanges: FiscalYearRange[];
    // True when the message explicitly asked for trade-date filtering.
    useTradeDate: boolean;
}

// Decide whether the message is a data-retrieval request at all. Returns
// undefined when no registry descriptor scored - the caller falls back
// to its existing intent handling (uploads, greetings, etc.).
export function detectQueryDescriptor(message: string): ChatQueryDescriptor | undefined {
    return pickQueryDescriptor(message);
}

// Normalise a chunk of text for fuzzy comparison against a customer
// dimension name. Lower-cases, collapses non-alphanumerics, and trims.
function normalize(value: string): string {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

// Score a candidate dimension name against the message. Higher is
// better. Returns 0 when nothing matches.
function scoreMatch(messageNormalized: string, candidate: string): number {
    const candidateNorm = normalize(candidate);
    if (!candidateNorm) return 0;
    if (messageNormalized === candidateNorm) return 1;
    if (messageNormalized.includes(` ${candidateNorm} `)) return 0.95;
    if (messageNormalized.startsWith(`${candidateNorm} `) || messageNormalized.endsWith(` ${candidateNorm}`)) return 0.9;
    if (messageNormalized.includes(candidateNorm)) return 0.8;
    // token-overlap fallback so multi-word names like "St. Louis" partial-match.
    const messageTokens = new Set(messageNormalized.split(' ').filter(Boolean));
    const candidateTokens = candidateNorm.split(' ').filter(Boolean);
    if (candidateTokens.length === 0) return 0;
    const overlap = candidateTokens.filter(t => messageTokens.has(t)).length;
    return overlap / candidateTokens.length * 0.6;
}

// Pull every customer dimension whose name appears in the message,
// ranked by match score. We keep all hits above the 0.6 threshold so a
// query like "corn and soybean positions" naturally filters on both.
function collectMatches<T extends Record<string, any>>(
    messageNormalized: string,
    items: T[] | null | undefined,
    nameField: keyof T,
): T[] {
    if (!items || items.length === 0) return [];
    const hits: { item: T; score: number }[] = [];
    for (const item of items) {
        const score = scoreMatch(messageNormalized, String(item[nameField] ?? ''));
        if (score >= 0.6) hits.push({ item, score });
    }
    hits.sort((a, b) => b.score - a.score);
    // Deduplicate by id-like field when the same name resolves twice.
    const seen = new Set<unknown>();
    const result: T[] = [];
    for (const h of hits) {
        const id = (h.item as any).commodityId
            ?? (h.item as any).locationId
            ?? (h.item as any).counterpartyId
            ?? (h.item as any).portfolioId;
        if (id == null || !seen.has(id)) {
            if (id != null) seen.add(id);
            result.push(h.item);
        }
    }
    return result;
}

// Detect "trade date" / "traded on" / "trades from X to Y" style
// language so we know to swap from the default budget-month filter to
// the trade-date filter.
function detectTradeDateUsage(message: string): boolean {
    const lower = message.toLowerCase();
    return /\btrade date\b|\btraded\s+on\b|\btraded\s+between\b|\btraded\s+from\b|\btrades\s+from\b|\btrades\s+between\b|\btrades?\s+on\s+\d/.test(lower);
}

// Extract an optional explicit date range. Supports YYYY-MM-DD,
// MM/DD/YYYY, and natural "today"/"yesterday". Used only when the
// message also mentioned trade date (see detectTradeDateUsage).
function extractDateRange(message: string): { start?: string; end?: string } {
    const dateRegex = /\b(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})\b/g;
    const matches: string[] = [];
    let m: RegExpExecArray | null;
    while ((m = dateRegex.exec(message)) !== null) matches.push(m[1]);
    const parsed = matches
        .map(s => {
            const d1 = dayjs(s, 'YYYY-MM-DD', true);
            if (d1.isValid()) return d1;
            const d2 = dayjs(s);
            return d2.isValid() ? d2 : undefined;
        })
        .filter((d): d is dayjs.Dayjs => !!d)
        .sort((a, b) => a.valueOf() - b.valueOf());
    if (parsed.length === 0) {
        const lower = message.toLowerCase();
        if (/\btoday\b/.test(lower)) {
            const today = dayjs().format('YYYY-MM-DD');
            return { start: today, end: today };
        }
        if (/\byesterday\b/.test(lower)) {
            const y = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
            return { start: y, end: y };
        }
        return {};
    }
    return {
        start: parsed[0].format('YYYY-MM-DD'),
        end: parsed[parsed.length - 1].format('YYYY-MM-DD'),
    };
}

// Detect position types referenced in the message ("show me futures",
// "swap positions", ...). Only applies to position-style descriptors -
// the registry's execute method ignores it where unsupported.
function extractPositionTypes(message: string): PositionType[] {
    const lower = message.toLowerCase();
    const map: Array<{ keyword: RegExp; value: PositionType }> = [
        { keyword: /\bfutures?\b/, value: 'Future' },
        { keyword: /\boptions?\b/, value: 'Option' },
        { keyword: /\bswaps?\b/, value: 'Swap' },
        { keyword: /\bphysicals?\b|\bcash\s+positions?\b/, value: 'Physical' },
        { keyword: /\bbasis\b/, value: 'Basis' },
        { keyword: /\bindex\b/, value: 'Index' },
        { keyword: /\bunderlying\b/, value: 'Underlying' },
        { keyword: /\ball[\s-]?in\b/, value: 'AllIn' },
    ];
    const hits: PositionType[] = [];
    for (const entry of map) {
        if (entry.keyword.test(lower)) hits.push(entry.value);
    }
    return hits;
}

// Detect buy/sell side hints. Returns undefined when both or neither
// were mentioned, which leaves the filter unset.
function extractSide(message: string): 'Buy' | 'Sell' | undefined {
    const lower = message.toLowerCase();
    const hasBuy = /\b(buy|bought|long|purchase|purchases)\b/.test(lower);
    const hasSell = /\b(sell|sold|short|sale|sales)\b/.test(lower);
    if (hasBuy && !hasSell) return 'Buy';
    if (hasSell && !hasBuy) return 'Sell';
    return undefined;
}

// Main entry point. The orchestrator calls this once it has decided the
// message is a data-retrieval request (descriptor != undefined).
export function parseChatQuery(
    message: string,
    customerData: GetCustomerDataResponse | undefined | null,
    customerSettings: GetCustomerSettingsResponse | undefined | null,
    descriptorOverride?: ChatQueryDescriptor,
): ParsedChatQuery | undefined {
    const descriptor = descriptorOverride ?? detectQueryDescriptor(message);
    if (!descriptor) return undefined;

    const normalized = ` ${normalize(message)} `;

    const commodityMatches = collectMatches<CommodityModel>(normalized, customerData?.commodities, 'commodityName');
    const locationMatches = collectMatches<LocationModel>(normalized, customerData?.locations, 'locationName');
    const counterpartyMatches = collectMatches<CounterpartyModel>(normalized, customerData?.counterparties, 'counterpartyName');
    const portfolioMatches = collectMatches<PortfolioModel>(normalized, customerData?.portfolios, 'portfolioName');

    const fiscalYears = parseAllFiscalYearsFromMessage(message);
    const fiscalYearRanges = fiscalYears.map(y => expandFiscalYearToBudgetMonthRange(y, customerSettings?.fiscalMonthOffset));

    const useTradeDate = detectTradeDateUsage(message);
    const explicitRange = useTradeDate ? extractDateRange(message) : {};

    const positionTypes = extractPositionTypes(message);
    const side = extractSide(message);

    const filters: ChatQueryFilters = {
        commodityIds: commodityMatches.map(c => c.commodityId).filter((x): x is number => x != null),
        commodityNames: commodityMatches.map(c => c.commodityName!).filter(Boolean),
        locationIds: locationMatches.map(l => l.locationId).filter((x): x is number => x != null),
        locationNames: locationMatches.map(l => l.locationName!).filter(Boolean),
        counterpartyIds: counterpartyMatches.map(c => c.counterpartyId).filter((x): x is number => x != null),
        counterpartyNames: counterpartyMatches.map(c => c.counterpartyName!).filter(Boolean),
        portfolioIds: portfolioMatches.map(p => p.portfolioId).filter((x): x is number => x != null),
        portfolioNames: portfolioMatches.map(p => p.portfolioName!).filter(Boolean),
        fiscalYears,
        fiscalYearRanges,
        tradeDateStart: explicitRange.start,
        tradeDateEnd: explicitRange.end,
        useTradeDate,
        positionTypes,
        side,
    };

    return {
        descriptor,
        filters,
        unresolvedNames: [], // future: collect proper-noun-like tokens that didn't match any dimension
        fiscalYearRanges,
        useTradeDate,
    };
}

// Re-export the registry helpers so ChatAssistant.tsx only needs to
// import from one module when wiring this feature in.
export { CHAT_QUERY_REGISTRY };
